/**
 * /tools/bid-for-connection?clientId=X — STP-037 (Should-priority).
 *
 * Weekly log of bids for connection (Gottman). Each bid = {text, who made
 * it, response}. Web computes towardRatio = toward / total.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

type MadeBy = 'self' | 'other';
type Response = 'toward' | 'away' | 'against';

interface BidRow {
  bidText: string;
  madeBy: MadeBy;
  response: Response;
  context: string;
}

interface FormState {
  bids: BidRow[];
  patternsObserved: string;
  nextWeekIntention: string;
}

const EMPTY: BidRow = { bidText: '', madeBy: 'self', response: 'toward', context: '' };
const DEFAULT_FORM: FormState = {
  bids: [{ ...EMPTY }, { ...EMPTY }, { ...EMPTY }],
  patternsObserved: '',
  nextWeekIntention: '',
};

export function BidForConnectionPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'bid-for-connection');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          if (Array.isArray(p.bids) && p.bids.length > 0) {
            next.bids = p.bids.map((raw) => {
              const b = raw as Record<string, unknown>;
              return {
                bidText: typeof b.bidText === 'string' ? b.bidText : '',
                madeBy: b.madeBy === 'other' ? 'other' : 'self',
                response:
                  b.response === 'away' ? 'away' : b.response === 'against' ? 'against' : 'toward',
                context: typeof b.context === 'string' ? b.context : '',
              };
            });
          }
          if (typeof p.patternsObserved === 'string') next.patternsObserved = p.patternsObserved;
          if (typeof p.nextWeekIntention === 'string') next.nextWeekIntention = p.nextWeekIntention;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const towardRatio = useMemo(() => {
    const valid = form.bids.filter((b) => b.bidText.trim().length > 0);
    if (valid.length === 0) return null;
    const toward = valid.filter((b) => b.response === 'toward').length;
    return toward / valid.length;
  }, [form.bids]);

  const canSubmit =
    !submitting &&
    hydrated &&
    form.bids.some((b) => b.bidText.trim().length > 0);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.bidForConnection.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      bids: form.bids
        .filter((b) => b.bidText.trim().length > 0)
        .map((b) => ({
          bidText: b.bidText.trim(),
          madeBy: b.madeBy,
          response: b.response,
          ...(b.context.trim() ? { context: b.context.trim() } : {}),
        })),
      ...(towardRatio !== null ? { towardRatio } : {}),
      ...(form.patternsObserved.trim() ? { patternsObserved: form.patternsObserved.trim() } : {}),
      ...(form.nextWeekIntention.trim() ? { nextWeekIntention: form.nextWeekIntention.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'bid-for-connection',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.bidForConnection.errors.generic'));
      } else {
        setSubmitError(t('tools.bidForConnection.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="bid-for-connection-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.bidForConnection.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.bidForConnection.errors.missingClient')}</p>
      </section>
    );
  }

  const setBid = <K extends keyof BidRow>(idx: number, key: K, value: BidRow[K]): void => {
    setForm((prev) => ({
      ...prev,
      bids: prev.bids.map((b, i) => (i === idx ? { ...b, [key]: value } : b)),
    }));
  };
  const addBid = (): void => {
    setForm((prev) =>
      prev.bids.length >= 50 ? prev : { ...prev, bids: [...prev.bids, { ...EMPTY }] },
    );
  };
  const removeBid = (idx: number): void => {
    setForm((prev) =>
      prev.bids.length <= 1
        ? prev
        : { ...prev, bids: prev.bids.filter((_, i) => i !== idx) },
    );
  };

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.bidForConnection.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.bidForConnection.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.bidForConnection.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.bidForConnection.bids.title')}
          </h2>
          <p className="mt-s2 text-gray-2 italic">{t('tools.bidForConnection.bids.hint')}</p>
          <ol className="mt-s3 space-y-s3">
            {form.bids.map((bid, idx) => (
              <li
                key={idx}
                className="rounded-md border border-bone-3 bg-bone p-s3 space-y-s2"
                data-testid={`bid-${idx}`}
              >
                <input
                  type="text"
                  value={bid.bidText}
                  onChange={(e) => setBid(idx, 'bidText', e.target.value)}
                  placeholder={t('tools.bidForConnection.placeholders.bidText')}
                  className="input-field w-full"
                  data-testid={`bid-text-${idx}`}
                />
                <div className="grid grid-cols-12 gap-s2">
                  <select
                    value={bid.madeBy}
                    onChange={(e) => setBid(idx, 'madeBy', e.target.value as MadeBy)}
                    className="input-field col-span-3"
                    data-testid={`bid-madeBy-${idx}`}
                    aria-label={t('tools.bidForConnection.fields.madeBy')}
                  >
                    <option value="self">{t('tools.bidForConnection.madeBy.self')}</option>
                    <option value="other">{t('tools.bidForConnection.madeBy.other')}</option>
                  </select>
                  <select
                    value={bid.response}
                    onChange={(e) => setBid(idx, 'response', e.target.value as Response)}
                    className="input-field col-span-4"
                    data-testid={`bid-response-${idx}`}
                    aria-label={t('tools.bidForConnection.fields.response')}
                  >
                    <option value="toward">{t('tools.bidForConnection.response.toward')}</option>
                    <option value="away">{t('tools.bidForConnection.response.away')}</option>
                    <option value="against">{t('tools.bidForConnection.response.against')}</option>
                  </select>
                  <input
                    type="text"
                    value={bid.context}
                    onChange={(e) => setBid(idx, 'context', e.target.value)}
                    placeholder={t('tools.bidForConnection.placeholders.context')}
                    className="input-field col-span-4"
                    data-testid={`bid-context-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeBid(idx)}
                    disabled={form.bids.length <= 1}
                    className="col-span-1 btn-ghost text-sm"
                    data-testid={`bid-remove-${idx}`}
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ol>
          <button
            type="button"
            onClick={addBid}
            disabled={form.bids.length >= 50}
            className="btn-ghost mt-s3"
            data-testid="bid-add"
          >
            + {t('tools.bidForConnection.bids.add')}
          </button>
          {towardRatio !== null && (
            <p className="mt-s3 font-mono text-sm text-flame-deep" data-testid="bid-toward-ratio">
              {t('tools.bidForConnection.towardRatio', { pct: Math.round(towardRatio * 100) })}
            </p>
          )}
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5 space-y-s3">
          <label className="block">
            <span className="form-label">{t('tools.bidForConnection.fields.patterns')}</span>
            <textarea
              value={form.patternsObserved}
              onChange={(e) => setForm((prev) => ({ ...prev, patternsObserved: e.target.value }))}
              placeholder={t('tools.bidForConnection.placeholders.patterns')}
              className="input-field mt-s2 min-h-20"
              data-testid="bid-patterns"
              rows={3}
            />
          </label>
          <label className="block">
            <span className="form-label">{t('tools.bidForConnection.fields.nextWeek')}</span>
            <textarea
              value={form.nextWeekIntention}
              onChange={(e) => setForm((prev) => ({ ...prev, nextWeekIntention: e.target.value }))}
              placeholder={t('tools.bidForConnection.placeholders.nextWeek')}
              className="input-field mt-s2 min-h-20"
              data-testid="bid-next-week"
              rows={3}
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="bid-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="bid-saved-at">
            ✓ {t('tools.bidForConnection.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <FinalizeWarning />

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.bidForConnection.submitting') : t('tools.bidForConnection.submit')}
        </button>
      </form>
    </section>
  );
}
